
import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Clock, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { TopMenu } from "@/components/TopMenu";

type TimePreferences = {
  [key: string]: {
    active: boolean;
    time: string;
  };
};

type SubscriberData = {
  subscriber_id: string;
  onboarding_stage: string;
  preferred_time_slots?: any;
};

const TimePreference = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [subscriberData, setSubscriberData] = useState<SubscriberData | null>(null);
  const [preferences, setPreferences] = useState<TimePreferences>({
    sunday: { active: false, time: "12:30" },
    monday: { active: false, time: "12:30" },
    tuesday: { active: false, time: "12:30" },
    wednesday: { active: false, time: "12:30" },
    thursday: { active: false, time: "12:30" },
    friday: { active: false, time: "12:30" },
    saturday: { active: false, time: "12:30" },
  });

  useEffect(() => {
    // Fetch current user and their preferences
    const fetchUserPreferences = async () => {
      try {
        setIsLoading(true);
        
        // Check if user is authenticated
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.log('No authenticated user found, redirecting to register');
          navigate('/register');
          return;
        }

        // Get the subscriber data for this user
        const { data: mappingData, error: mappingError } = await supabase
          .from('user_psid_mappings')
          .select('subscriber_id')
          .eq('user_email', user.email)
          .single();

        if (mappingError || !mappingData?.subscriber_id) {
          console.log('No subscriber mapping found, redirecting to registration');
          toast({
            title: "Registration Required",
            description: "Please complete your registration first.",
            variant: "destructive",
          });
          navigate('/register');
          return;
        }

        // Get the subscriber record with preferences
        const { data: subscriber, error: subscriberError } = await supabase
          .from('subscribers')
          .select('subscriber_id, preferred_time_slots, onboarding_stage')
          .eq('subscriber_id', mappingData.subscriber_id)
          .single();

        if (subscriberError) {
          console.error('Error fetching subscriber data:', subscriberError);
          toast({
            title: "Error",
            description: "Could not load your preferences. Please try again.",
            variant: "destructive",
          });
          return;
        }

        setSubscriberData(subscriber);

        // Map the database format to our component state
        if (subscriber.preferred_time_slots) {
          const mappedPreferences: TimePreferences = { ...preferences };
          
          Object.entries(subscriber.preferred_time_slots).forEach(([day, prefs]: [string, any]) => {
            mappedPreferences[day] = {
              active: prefs.active || false,
              time: prefs.call_time_1 || "12:30"
            };
          });
          
          setPreferences(mappedPreferences);
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
        toast({
          title: "Error",
          description: "Failed to load your preferences. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserPreferences();
  }, [navigate, toast]);
  
  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  const handleSavePreferences = async () => {
    const isAnyDayActive = Object.values(preferences).some(pref => pref.active);
    
    if (!isAnyDayActive) {
      toast({
        title: "Validation Error",
        description: "Please enable at least 1 day for scheduling",
        variant: "destructive",
      });
      return; // Prevent further execution
    }

    try {
      if (!subscriberData?.subscriber_id) {
        throw new Error('No subscriber ID found');
      }

      setIsLoading(true);
      
      // Transform preferences to match database schema
      const formattedPreferences = Object.entries(preferences).reduce((acc, [day, pref]) => ({
        ...acc,
        [day]: {
          active: pref.active,
          call_time_1: pref.active ? pref.time : null,
          call_time_2: null
        }
      }), {});

      // Determine if we should update the onboarding_stage
      let onboarding_stage = subscriberData.onboarding_stage;
      if (onboarding_stage === 'payment_complete') {
        onboarding_stage = 'active';
      }

      // Update subscriber preferences and stage if needed
      const { data, error } = await supabase.functions.invoke('update-subscriber', {
        body: { 
          subscriberData: {
            subscriber_id: subscriberData.subscriber_id,
            preferred_time_slots: formattedPreferences,
            onboarding_stage
          }
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your time preferences have been saved.",
      });
      
      // If onboarding stage was updated to active, let the user know they've completed setup
      if (onboarding_stage === 'active' && subscriberData.onboarding_stage !== 'active') {
        toast({
          title: "Setup Complete!",
          description: "Your account is now active. You'll receive calls according to your preferences.",
        });
      }
      
      // Update the local state with the new onboarding stage
      setSubscriberData({
        ...subscriberData,
        onboarding_stage
      });

      // Navigate to /test-call after saving preferences
      navigate('/test-call');

    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save your preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeChange = (day: string, time: string) => {
    setPreferences(prev => ({
      ...prev,
      [day]: { ...prev[day], time }
    }));
  };

  const handleToggleDay = (day: string) => {
    setPreferences(prev => ({
      ...prev,
      [day]: { ...prev[day], active: !prev[day].active }
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <TopMenu />
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-sm">
          <h1 className="text-2xl font-semibold mb-8">Select Your Preferred Time Slot</h1>
          
          <div className="space-y-6">
            {Object.entries(preferences).map(([day, { active, time }]) => (
              <div key={day} className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Switch
                    checked={active}
                    onCheckedChange={() => handleToggleDay(day)}
                  />
                  <span className="text-lg capitalize">{day}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="time"
                    className={`px-3 py-1 rounded border ${active ? 'border-gray-300' : 'border-gray-200 bg-gray-50'}`}
                    value={time}
                    onChange={(e) => handleTimeChange(day, e.target.value)}
                    disabled={!active}
                  />
                  <Clock className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>

          <Button 
            className="w-full mt-8 bg-black hover:bg-gray-800"
            onClick={handleSavePreferences}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Preference'
            )}
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default TimePreference;
